import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Dither from './Dither';
import { projects } from '../data/projects';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

export default function Projects() {
    const { language } = useLanguage();
    const t = translations[language];
    const currentProjects = projects[language];
    const [selectedProject, setSelectedProject] = useState(null);

    // Remove auto-scroll useEffect

    return (
        <section id="projects" className="w-full bg-background border-t-brut relative overflow-hidden z-0">
            
            {/* Pill-shaped Dither Window */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140vw] h-[80vw] md:w-[80vw] md:h-[40vw] rounded-full overflow-hidden z-0 opacity-80 border border-white/5 shadow-[0_0_80px_rgba(56,189,248,0.15)] pointer-events-auto rotate-12 transition-all duration-700">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]} 
                    disableAnimation={false}
                    enableMouseInteraction
                    mouseRadius={0.3}
                    colorNum={4}
                    waveAmplitude={0.3}
                    waveFrequency={3}
                    waveSpeed={0.05}
                />
            </div>

            {/* Compact Header */}
            <div className="flex justify-between items-center px-6 md:px-12 py-8 border-b border-border relative z-10 bg-black/40 backdrop-blur-md">
                <div className="flex items-center gap-4">
                     <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted">{t.projects_selected_work}</span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                    {currentProjects.length} {t.projects_clients}
                </span>
            </div>
            
            {/* Section Title Massive */}
            <div className="px-6 md:px-12 py-8 md:py-20 border-b border-border min-h-[15vh] md:min-h-[40vh] flex items-center relative overflow-hidden group/title">
                {/* Subtle warp glow behind title */}
                <div className="absolute -left-[10%] top-1/2 -translate-y-1/2 w-[30vw] h-[30vw] bg-accent/10 rounded-full blur-[100px] opacity-0 group-hover/title:opacity-100 transition-opacity duration-1000 pointer-events-none mix-blend-screen" />
                    <motion.h2 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="text-[clamp(3rem,12vw,12rem)] font-heading uppercase font-extrabold text-foreground tracking-tighter leading-[0.85] relative z-10"
                >
                    {t.projects_our}<br/>{t.projects_work}
                </motion.h2>
            </div>

            {/* Sleek List Project Layout */}
            <div className="flex flex-col w-full border-t border-border">
                {currentProjects.map((project, index) => (
                    <motion.div
                        key={`${project.id}`}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                        onClick={() => setSelectedProject(project)}
                        className="group relative border-b border-border/30 cursor-pointer overflow-hidden bg-background hover:bg-white/[0.02] transition-colors duration-500 flex flex-col md:flex-row items-start md:items-center px-6 md:px-12 py-8 md:py-12 w-full"
                    >
                        {/* Desktop Hover Image - Hidden by default, appears and rotates on hover */}
                        <div className="absolute right-6 md:right-32 top-1/2 -translate-y-1/2 w-[280px] md:w-[400px] aspect-[4/3] opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-700 pointer-events-none z-0 overflow-hidden rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] hidden md:block -rotate-6 group-hover:rotate-2">
                            <img src={project.img} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 grayscale group-hover:grayscale-0" />
                            <div className="absolute inset-0 border border-white/10 rounded-2xl" />
                        </div>

                        {/* Mobile Image Thumbnail */}
                        <div className="w-full h-[180px] mb-6 rounded-xl overflow-hidden md:hidden relative z-10">
                            <img src={project.img} alt={project.title} className="w-full h-full object-cover filter grayscale" />
                            <div className="absolute inset-0 bg-black/20" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 relative z-10 flex flex-col md:flex-row md:items-center justify-between w-full pointer-events-none">
                            <div className="flex flex-col">
                                <div className="flex items-center gap-4 mb-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-accent">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                                        {project.category}
                                    </span>
                                </div>
                                <h3 className="text-4xl md:text-6xl lg:text-7xl font-heading font-extrabold uppercase tracking-tighter text-foreground/80 group-hover:text-foreground transition-all duration-500 md:group-hover:translate-x-6">
                                    {project.title}
                                </h3>
                            </div>

                            <div className="mt-6 md:mt-0 flex items-center justify-between md:justify-end w-full md:w-auto gap-4 md:gap-12">
                                <span className="font-mono text-sm uppercase tracking-widest text-muted group-hover:text-foreground transition-colors duration-500">
                                    {project.year}
                                </span>
                                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full border border-border/50 flex items-center justify-center group-hover:bg-foreground group-hover:text-background md:group-hover:border-foreground transition-all -rotate-45 group-hover:rotate-0 duration-500 bg-background/50 backdrop-blur-sm">
                                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Popup Modal */}
            <AnimatePresence>
                {selectedProject && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-6"
                        onClick={() => setSelectedProject(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            className={`bg-background border border-border rounded-lg ${selectedProject.link ? 'max-w-6xl h-[85vh]' : 'max-w-md p-8'} w-full relative overflow-hidden flex flex-col`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button 
                                onClick={() => setSelectedProject(null)} 
                                className="absolute top-4 right-4 z-50 bg-black/50 backdrop-blur-md rounded-full p-2 text-white hover:text-accent transition-colors cursor-pointer border border-white/10"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            {selectedProject.link ? (
                                <>
                                    {/* Web Preview Mode */}
                                    <div className="w-full h-12 bg-black/80 backdrop-blur-md border-b border-white/10 flex items-center px-6 gap-4 z-40 shrink-0">
                                        <div className="flex gap-2">
                                            <div className="w-3 h-3 rounded-full bg-red-500/80" />
                                            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                                            <div className="w-3 h-3 rounded-full bg-green-500/80" />
                                        </div>
                                        <div className="flex-1 text-center font-mono text-[10px] text-white/50 tracking-widest uppercase truncate hidden md:block">
                                            {selectedProject.title} — {selectedProject.link}
                                        </div>
                                        <a 
                                            href={selectedProject.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-[10px] text-accent hover:text-white transition-colors uppercase tracking-widest flex items-center gap-1"
                                        >
                                            {t.projects_open_in_new_tab} <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                        </a>
                                    </div>
                                    <div className="flex-1 w-full relative bg-white">
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/90 text-white font-mono text-[10px] tracking-widest z-0">
                                            {t.projects_loading_interface}
                                        </div>
                                        <iframe 
                                            src={selectedProject.link} 
                                            title={selectedProject.title}
                                            className="relative z-10 w-full h-full border-0"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="w-full bg-black/90 backdrop-blur-md border-t border-white/10 p-4 shrink-0 flex flex-col md:flex-row items-center justify-between gap-4 z-40">
                                        <div className="flex-1">
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-muted block mb-1">{t.projects_mission}</span>
                                            <p className="font-mono text-[10px] text-foreground/80 leading-relaxed line-clamp-1">{selectedProject.details?.mission}</p>
                                        </div>
                                        <div className="flex-1 hidden md:block">
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-muted block mb-1">{t.projects_execution}</span>
                                            <p className="font-mono text-[10px] text-foreground/80 leading-relaxed line-clamp-1">{selectedProject.details?.execution}</p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter text-foreground leading-none mb-4 pr-8">
                                        {selectedProject.title}
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-muted block mb-1">{t.projects_mission}</span>
                                            <p className="font-mono text-xs text-foreground/80 leading-relaxed">
                                                {selectedProject.details?.mission}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-muted block mb-1">{t.projects_execution}</span>
                                            <p className="font-mono text-xs text-foreground/80 leading-relaxed">
                                                {selectedProject.details?.execution}
                                            </p>
                                        </div>
                                        <div className="flex items-center justify-between pt-4 border-t border-border">
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-accent">
                                                {selectedProject.details?.status}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}